import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtCurrency, ROLES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Package, ShoppingCart, Receipt, TrendingUp, AlertTriangle, Users, ChevronRight } from "lucide-react";

/**
 * Stat card. If `to` is set, becomes a clickable Link that drills into a
 * filtered list view. Non-clickable for stats without a natural destination.
 */
function Stat({ label, value, icon: Icon, accent, to }) {
  const testid = `stat-${label.toLowerCase().replace(/\s+/g, "-")}`;
  const inner = (
    <>
      <div>
        <div className="label-eyebrow flex items-center gap-1">
          {label}
          {to && <ChevronRight className="w-3 h-3 text-zinc-400 group-hover:translate-x-0.5 transition-transform" />}
        </div>
        <div className="mt-3 font-mono text-3xl font-bold text-zinc-950 tracking-tight">{value}</div>
      </div>
      <div className={`w-10 h-10 ${accent || "bg-zinc-100"} flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-zinc-700" strokeWidth={1.5} />
      </div>
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testid} className="group brutalist-card p-6 flex items-start justify-between hover:bg-zinc-50 transition-colors">
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={testid} className="brutalist-card p-6 flex items-start justify-between">
      {inner}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/stats/dashboard").then(({ data }) => setStats(data)).finally(() => setLoading(false));
  }, []);

  const isRetail = user.role === "retail_counter";

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <div className="flex items-end justify-between border-b border-zinc-200 pb-6">
        <div>
          <div className="label-eyebrow mb-2">Workspace</div>
          <h1 className="text-4xl font-bold tracking-tight text-zinc-950">
            {user.role === "retail_counter" ? "Retail Counter" : ROLES[user.role]} Dashboard
          </h1>
          <p className="text-sm text-zinc-500 mt-2">Hello, {user.name}. Here is your activity snapshot.</p>
        </div>
        <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">
          {new Date().toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
        </div>
      </div>

      {loading ? (
        <div className="text-sm font-mono text-zinc-500">Loading...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
            {stats.parts_count !== undefined && (
              <Stat label="Parts in catalog" value={stats.parts_count} icon={Package} accent="bg-blue-50" to="/parts" />
            )}
            {stats.pending_orders !== undefined && (
              <Stat label="Pending orders" value={stats.pending_orders} icon={ShoppingCart} accent="bg-amber-50" to="/orders?status=pending" />
            )}
            {stats.unpaid_invoices !== undefined && (
              <Stat label="Unpaid invoices" value={stats.unpaid_invoices} icon={Receipt} accent="bg-rose-50" to="/invoices?status=unpaid" />
            )}
            {stats.revenue_month !== undefined && (
              <Stat label="Revenue (MTD)" value={fmtCurrency(stats.revenue_month)} icon={TrendingUp} accent="bg-emerald-50" to="/reports" />
            )}
            {stats.users_count !== undefined && (
              <Stat label="Total users" value={stats.users_count} icon={Users} accent="bg-zinc-100" to="/users" />
            )}
            {stats.my_orders !== undefined && (
              <>
                <Stat label="My orders" value={stats.my_orders} icon={ShoppingCart} accent="bg-blue-50" to={isRetail ? "/orders" : undefined} />
                <Stat label="My pending" value={stats.my_pending} icon={AlertTriangle} accent="bg-amber-50" to={isRetail ? "/orders?status=pending" : undefined} />
                <Stat label="My unpaid" value={stats.my_unpaid} icon={Receipt} accent="bg-rose-50" to={isRetail ? "/invoices?status=unpaid" : undefined} />
              </>
            )}
          </div>

          {stats.low_stock && stats.low_stock.length > 0 && (
            <div className="brutalist-card">
              <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                <div>
                  <div className="label-eyebrow">Inventory Alert</div>
                  <h3 className="text-lg font-semibold text-zinc-950 mt-1">Low stock items</h3>
                </div>
                <span className="font-mono text-xs px-2 py-1 bg-amber-50 border border-amber-200 text-amber-800 uppercase tracking-wider">Threshold ≤ 5</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[11px]">
                    <th className="text-left px-6 py-3 font-semibold">Part #</th>
                    <th className="text-left px-6 py-3 font-semibold">Manufacturer</th>
                    <th className="text-left px-6 py-3 font-semibold">Name</th>
                    <th className="text-right px-6 py-3 font-semibold">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.low_stock.map((p) => (
                    <tr key={p.id} className="border-t border-zinc-200">
                      <td className="px-6 py-3 font-mono text-zinc-950">{p.part_number}</td>
                      <td className="px-6 py-3 text-zinc-700">{p.manufacturer}</td>
                      <td className="px-6 py-3 text-zinc-700">{p.name || "—"}</td>
                      <td className="px-6 py-3 text-right font-mono font-semibold text-rose-600">{p.stock}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
